import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { HardwareTypeDetailState } from './useHardwareTypeDetail';

/** The Model references tab: the revisions of this type, and the way to add one. */
export const HardwareTypeModelRefsTab: React.FC<{ state: HardwareTypeDetailState; canEdit: boolean }> = ({ state, canEdit }) => {
  const { formatNumber, t } = useI18n();
  const { localModelRefs, availableRefs, availableModelRefs, isLoading } = state;

  return (
    <div className="tab-content">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('deviceTypes.modelReferences')}<span className="ud-tab-count">{formatNumber(localModelRefs.length)}</span></h3>
          <p>{t('deviceTypes.detail.refsIntro')}</p>
        </header>

        {localModelRefs.length > 0 ? (
          <table className="tpd-table dt-refs-table">
            <thead>
              <tr>
                <th>{t('deviceTypes.code')}</th>
                <th>{t('deviceTypes.name')}</th>
                <th>{t('deviceTypes.detail.statusLabel')}</th>
                {canEdit && <th className="dt-col-actions"><span className="sr-only">{t('deviceTypes.actions')}</span></th>}
              </tr>
            </thead>
            <tbody>
              {localModelRefs.map((ref) => (
                <tr key={ref.id} data-testid={`detail-model-ref-${ref.id}`}>
                  <td><span className="type-badge tpl-type">{ref.code}</span></td>
                  <td>{ref.name || <span className="tpd-muted">–</span>}</td>
                  <td>
                    <span className={`status-badge ${ref.is_active ? 'active' : 'inactive'}`}>
                      {ref.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="dt-col-actions">
                      <button type="button" className="tpl-link is-danger" onClick={() => state.removeModelRef(ref.id)} disabled={isLoading} data-testid={`btn-remove-model-ref-${ref.id}`}>
                        {t('deviceTypes.detail.remove')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="tpd-empty"><p>{t('deviceTypes.noModelReferencesFound')}</p></div>
        )}

        {canEdit && (
          <div className="cu-field dt-add-ref">
            <label htmlFor="hwAddRef">{t('deviceTypes.addModelReference')}</label>
            <div className="cu-input-row">
              <select
                id="hwAddRef"
                className="cu-input"
                data-testid="field-hardware-type-add-ref"
                value={state.selectedRefCode}
                onChange={(e) => state.setSelectedRefCode(e.target.value)}
                disabled={isLoading || availableRefs.length === 0}
              >
                <option value="">{t('deviceTypes.detail.refPlaceholder')}</option>
                {availableRefs.map((ref) => (
                  <option key={ref.id} value={ref.code}>{ref.code}{ref.name ? ` – ${ref.name}` : ''}</option>
                ))}
              </select>
              <button type="button" className="btn btn-primary btn-sm" onClick={state.addModelRef} disabled={isLoading || !state.selectedRefCode} data-testid="btn-hardware-type-add-ref">
                {isLoading ? t('users.adding') : t('deviceTypes.detail.add')}
              </button>
            </div>
            {availableRefs.length === 0 && availableModelRefs.length > 0 && <span className="cu-hint">{t('deviceTypes.allModelRefsAdded')}</span>}
            {availableModelRefs.length === 0 && <span className="cu-hint">{t('deviceTypes.noModelRefsYet')}</span>}
          </div>
        )}
      </section>
    </div>
  );
};
